/**
 * @file Full-page interstitial shown to members of v2-migrated orgs when they
 * open any protected v1 route. Explains the upgrade and redirects to the new
 * dashboard — automatically after a short countdown, or immediately via the
 * button. Rendered by ProtectedRoute in place of the requested page, so there
 * is no way to browse v1 while the org is on the migrated list.
 *
 * English-only by design: the interstitial is org-scoped (see
 * constants/v2Migration.js) and every migrated org so far operates in English.
 */
import React, { Component } from "react";
import { V2_DASHBOARD_URL } from "../../constants/v2Migration";

const REDIRECT_SECONDS = 10;

const styles = {
  page: {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f4f6f5",
    zIndex: 10000,
    padding: "24px"
  },
  card: {
    maxWidth: "480px",
    width: "100%",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
    padding: "40px 36px",
    textAlign: "center",
    fontFamily: "inherit"
  },
  logo: { width: "150px", marginBottom: "28px" },
  heading: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#1c2b27",
    marginBottom: "12px"
  },
  body: {
    fontSize: "15px",
    lineHeight: 1.6,
    color: "#4a5a55",
    marginBottom: "28px"
  },
  button: {
    display: "inline-block",
    background: "#0f9d76",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 28px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer"
  },
  countdown: {
    marginTop: "16px",
    fontSize: "13px",
    color: "#8a9691"
  }
};

class V2UpgradeInterstitial extends Component {
  constructor(props) {
    super(props);
    this.state = { secondsLeft: REDIRECT_SECONDS };
    this.interval = null;
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.setState(prev => {
        if (prev.secondsLeft <= 1) {
          this.goToV2();
          return { secondsLeft: 0 };
        }
        return { secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  goToV2() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    // replace() keeps v1 out of the back stack so Back doesn't bounce the
    // user straight into the interstitial again.
    window.location.replace(V2_DASHBOARD_URL);
  }

  render() {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img
            style={styles.logo}
            src={require("../../assets/images/remidio.svg")}
            alt="Remidio"
          />
          <div style={styles.heading}>Your experience has been upgraded</div>
          <p style={styles.body}>
            Your organization has moved to the new Remidio Connect dashboard,
            with faster search, richer screening insights and an improved
            patient view. We recommend using the new dashboard from now on
            &mdash; your login credentials stay the same.
          </p>
          <button
            type="button"
            style={styles.button}
            onClick={() => this.goToV2()}
          >
            Go to the new dashboard
          </button>
          <div style={styles.countdown}>
            Redirecting automatically in {this.state.secondsLeft}s&hellip;
          </div>
        </div>
      </div>
    );
  }
}

export default V2UpgradeInterstitial;
